/**
 * Sana va son formatlash — bitta joyda. Panel Toshkent vaqtida ishlaydi:
 * adminlar shu zonada va yetkazish vaqtlarini solishtirish oson bo'lsin.
 */
export const ADMIN_TIME_ZONE = "Asia/Tashkent";

const INTL_LOCALES: Record<string, string> = { uz: "uz", ru: "ru-RU", en: "en-GB" };

/**
 * O'zbekcha oy nomlari qo'lda: brauzer ICU ma'lumotlarida `uz` ko'pincha
 * to'liq emas va Intl "M09 29" kabi aralash natija beradi.
 */
const UZ_MONTHS = [
  "yanvar",
  "fevral",
  "mart",
  "aprel",
  "may",
  "iyun",
  "iyul",
  "avgust",
  "sentabr",
  "oktabr",
  "noyabr",
  "dekabr",
];

interface Parts {
  year: number;
  month: number;
  day: number;
  hour: string;
  minute: string;
}

function partsOf(date: Date, timeZone: string): Parts {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type: string) => parts.find(p => p.type === type)?.value ?? "";
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: get("hour"),
    minute: get("minute"),
  };
}

/** `2026-09-29T09:05:00Z` → "29 sentabr 2026, 14:05". `null` → "—". */
export function formatDateTime(
  iso: string | null | undefined,
  locale: string,
  timeZone = ADMIN_TIME_ZONE,
): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  if (locale === "uz") {
    const p = partsOf(date, timeZone);
    return `${p.day} ${UZ_MONTHS[p.month - 1]} ${p.year}, ${p.hour}:${p.minute}`;
  }
  return new Intl.DateTimeFormat(INTL_LOCALES[locale] ?? locale, {
    timeZone,
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(date);
}

/** Kalendar kuni `YYYY-MM-DD` → "29 sen" (grafik o'qi uchun qisqa). */
export function formatShortDay(day: string, locale: string): string {
  const [y, m, d] = day.split("-").map(Number);
  if (!y || !m || !d) return day;
  if (locale === "uz") return `${d} ${UZ_MONTHS[m - 1].slice(0, 3)}`;
  return new Intl.DateTimeFormat(INTL_LOCALES[locale] ?? locale, {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Minglik ajratgich bilan butun son: 12 345. */
export function formatNumber(value: number): string {
  return Math.round(value)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/** Foiz: 0..100 oralig'ida, butun. */
export function percent(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((part / total) * 100)));
}

/** Foydalanuvchi nomi: ism-familiya → @username → Telegram id. */
export function displayName(user: {
  firstName: string | null;
  lastName?: string | null;
  telegramUsername: string | null;
  telegramUserId?: string;
}): string {
  const full = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  if (full) return full;
  if (user.telegramUsername) return `@${user.telegramUsername}`;
  return user.telegramUserId ?? "—";
}

/** `YYYY-MM-DD` → "sentabr 2026" (oy sahifasi sarlavhasi). */
export function formatMonthYear(day: string, locale: string): string {
  const [y, m] = day.split("-").map(Number);
  if (!y || !m) return day;
  if (locale === "uz") return `${UZ_MONTHS[m - 1]} ${y}`;
  return new Intl.DateTimeFormat(INTL_LOCALES[locale] ?? locale, {
    timeZone: "UTC",
    month: "long",
    year: "numeric",
  }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** Hozirdan qancha oldin — kunlarda (0 — bugun). Kelajak ham 0. */
export function daysSince(iso: string, now = Date.now()): number {
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 86_400_000));
}
